import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useId,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  MarkerF,
  Autocomplete as GAutocomplete,
} from "@react-google-maps/api";
import { toast } from "sonner";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { MAP_LIBRARIES } from "../utils/mapLibraries";

const libraries = MAP_LIBRARIES;
const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "10px",
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.209,
};

const AUTOCOMPLETE_FIELDS = [
  "address_components",
  "formatted_address",
  "geometry",
  "name",
];

// "Farīdābād", "faridabad " and "Faridabad District" all collapse to the same key
// so values coming from Google, the postal API and CountriesNow can be compared.
const normalizeName = (value) =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\bdistrict\b/g, "")
    .replace(/[^a-z0-9]/g, "");

// Keeps the first spelling seen for a name, so pass the trusted values first.
const mergeDistrictOptions = (preferred = [], others = []) => {
  const byKey = new Map();
  [...preferred, ...others].forEach((name) => {
    const label = (name || "").trim();
    const key = normalizeName(label);
    if (key && !byKey.has(key)) byKey.set(key, label);
  });
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
};

// The postal API is authoritative for a pincode; the Google hint only decides
// which office to trust when a pincode spans more than one district.
const pickDistrict = (postalDistricts = [], hint = "") => {
  if (!postalDistricts.length) return hint;
  const hintKey = normalizeName(hint);
  const matched = postalDistricts.find((name) => normalizeName(name) === hintKey);
  return matched || postalDistricts[0];
};

const isSameLatLng = (a, b) =>
  !!a && !!b && Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lng - b.lng) < 1e-5;

const DistrictCombobox = ({ id, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    const query = normalizeName(value);
    if (!query) return options;
    const startsWith = [];
    const contains = [];
    options.forEach((option) => {
      const key = normalizeName(option);
      if (key.startsWith(query)) startsWith.push(option);
      else if (key.includes(query)) contains.push(option);
    });
    return [...startsWith, ...contains];
  }, [options, value]);

  const select = (name) => {
    onChange(name);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!filtered.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((prev) => (prev + step + filtered.length) % filtered.length);
    } else if (event.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        event.preventDefault();
        select(filtered[activeIndex]);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        id={id}
        type="text"
        value={value}
        className="form-control"
        placeholder="Search District"
        autoComplete="off"
        style={{ paddingRight: "32px" }}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />

      <Icon
        icon="mdi:chevron-down"
        width="20"
        height="20"
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--app-text-muted)",
          pointerEvents: "none",
        }}
      />

      {open && filtered.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            margin: "4px 0 0",
            padding: "4px 0",
            listStyle: "none",
            maxHeight: "200px",
            overflowY: "auto",
            background: "var(--app-surface)",
            border: "1px solid var(--app-border)",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {filtered.map((option, index) => (
            <li
              key={option}
              role="option"
              aria-selected={normalizeName(option) === normalizeName(value)}
              onMouseDown={(event) => {
                event.preventDefault();
                select(option);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                cursor: "pointer",
                color: "var(--app-text)",
                background:
                  index === activeIndex ? "var(--app-hover)" : "transparent",
              }}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

DistrictCombobox.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

DistrictCombobox.defaultProps = {
  id: undefined,
  value: "",
  options: [],
};

const GoogleMapWithSearch = ({ onAddressChange }) => {
  const apiKey = "AIzaSyC2k1lKoyNg_Kj5SoC2z2hCROopjGxB93Y"; // keep in env in production
  const districtInputId = useId();
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
  const [address, setAddress] = useState("");
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const [showmap, setShowmap] = useState(true);
  const [district, setDistrict] = useState("");
  const [districts, setDistricts] = useState([]);
  // raw administrative area from Google, kept apart from the value bound to the
  // field so a pincode lookup can refine it without fighting the user's choice
  const [geocodeDistrict, setGeocodeDistrict] = useState("");

  // editable fields
  const [floor, setFloor] = useState("");
  const [area, setArea] = useState("");
  const [pincode, setPincode] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const lastGeocodedLocationRef = useRef(null);
  const pincodeLookupSeqRef = useRef(0);
  const districtOptionsSeqRef = useRef(0);
  const stateCitiesRef = useRef([]);
  const stateCitiesCacheRef = useRef(new Map());
  const preferredDistrictsRef = useRef([]);
  const resolvedDistrictRef = useRef({ pin: "", district: "" });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  useEffect(() => {
    if (!isLoaded) return;
    geocoderRef.current = new window.google.maps.Geocoder();

    // try to use browser geolocation to center map initially
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setMapCenter(pos);
        setMarkerPosition(pos);
        // best-effort initial prefill
        doGeocodeAndSetFields(pos);
      },
      (error) => {
        console.error("Error getting geolocation", error);
        // still try to geocode default center
        doGeocodeAndSetFields(defaultCenter);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const parseAddressComponents = (components) => {
    // returns { city, state, country, pincode, areaCandidate, floorCandidate }
    let city = "";
    let state = "";
    let country = "";
    let pincode = "";
    let areaCandidate = "";
    let floorCandidate = "";
    let district = "";
    let districtFallback = "";

    components.forEach((component) => {
      const types = component.types || [];

      if (types.includes("postal_code")) {
        pincode = component.long_name;
      }
      if (types.includes("administrative_area_level_2")) {
        district = component.long_name;
      } else if (types.includes("administrative_area_level_3")) {
        districtFallback = component.long_name;
      }

      // city: try locality > postal_town > admin area level 2 (district) > neighborhood
      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("postal_town") && !city) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_2") && !city) {
        city = component.long_name;
      } else if (types.includes("neighborhood") && !city) {
        city = component.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
      }

      if (types.includes("country")) {
        country = component.long_name;
      }

      // area-like pieces
      if (
        (types.includes("sublocality") ||
          types.includes("sublocality_level_1") ||
          types.includes("neighborhood") ||
          types.includes("route")) &&
        !areaCandidate
      ) {
        areaCandidate = component.long_name;
      }

      if (
        (types.includes("street_number") ||
          types.includes("subpremise") ||
          types.includes("premise")) &&
        !floorCandidate
      ) {
        floorCandidate = component.long_name;
      }
    });

    return {
      city,
      district: district || districtFallback,
      state,
      country,
      pincode,
      areaCandidate,
      floorCandidate,
    };
  };

  // Every field is replaced, never merged with the previous location. Carrying a
  // stale pincode/city forward was what made the district belong to a different
  // city than the one that was searched.
  const applyParsedLocation = (formatted, parsed, latLng) => {
    const nextPincode = parsed.pincode || "";
    const hint = parsed.district || "";
    // a nearby point in the same pincode keeps the district the postal API already
    // confirmed, so nudging the map cannot blank out or re-spell a settled value
    const resolved = resolvedDistrictRef.current;
    const keepResolved =
      resolved.district && resolved.pin && resolved.pin === nextPincode;

    setAddress(formatted || "");
    setCity(parsed.city || "");
    setState(parsed.state || "");
    setCountry(parsed.country || "");
    setPincode(nextPincode);
    setArea(parsed.areaCandidate || "");
    setFloor(parsed.floorCandidate || "");
    setLatitude(latLng.lat);
    setLongitude(latLng.lng);
    setGeocodeDistrict(hint);
    setDistrict(keepResolved ? resolved.district : hint);
  };

  // wrap geocoder in a promise so callers can await completion
  const geocodeLatLng = (latLng) => {
    return new Promise((resolve) => {
      if (!geocoderRef.current) {
        resolve(null);
        return;
      }

      // avoid repeating geocode for the same spot
      if (isSameLatLng(lastGeocodedLocationRef.current, latLng)) {
        resolve(null);
        return;
      }

      const target = { lat: latLng.lat, lng: latLng.lng };
      lastGeocodedLocationRef.current = target;

      geocoderRef.current.geocode({ location: latLng }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const place = results[0];
          const formatted = place.formatted_address || "";
          const parsed = parseAddressComponents(place.address_components || []);

          applyParsedLocation(formatted, parsed, target);

          resolve({ formattedAddress: formatted, parsed, latLng: target });
        } else {
          console.error("Geocoder failed", status);
          resolve(null);
        }
      });
    });
  };

  const doGeocodeAndSetFields = (latLng) => {
    // fire & forget; we already have geocodeLatLng which sets state
    geocodeLatLng(latLng).catch((err) => console.error(err));
  };

  const fetchAddress = (latLng) => {
    return geocodeLatLng(latLng);
  };

  const handlePlaceChanged = () => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;
    const place = autocomplete.getPlace();

    if (!place) return;

    // If the place has geometry, use it; otherwise try geocode search result.
    if (place.geometry && place.geometry.location) {
      const newLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setMapCenter(newLocation);
      setMarkerPosition(newLocation);

      // If Google provides formatted_address & address_components we can parse directly
      const formatted = place.formatted_address || "";
      const components = place.address_components || [];
      if (components && components.length) {
        const parsed = parseAddressComponents(components);
        // the picked place is more precise than a reverse geocode of the same
        // point, so mark it as done and let the map settle without re-fetching
        lastGeocodedLocationRef.current = newLocation;
        applyParsedLocation(formatted, parsed, newLocation);
      } else {
        // fallback: geocode the location
        geocodeLatLng(newLocation).catch((err) => console.error(err));
      }
    } else {
      // no geometry — try to geocode current center
      geocodeLatLng(mapCenter).catch((err) => console.error(err));
    }
  };

  const onLoadAutocomplete = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const mapRef = useRef(null);
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMapIdle = () => {
    if (!mapRef.current) return;
    const center = mapRef.current.getCenter();
    const lat = center.lat();
    const lng = center.lng();
    const newLocation = { lat, lng };

    // If unchanged, only update lat/lng and skip heavy geocode
    if (isSameLatLng(lastGeocodedLocationRef.current, newLocation)) {
      setMapCenter(newLocation);
      setMarkerPosition(newLocation);
      setLatitude(lat);
      setLongitude(lng);
      return;
    }

    setMapCenter(newLocation);
    setMarkerPosition(newLocation);
    geocodeLatLng(newLocation).catch((err) => console.error(err));
  };

  // When user clicks "Add Complete Address", ensure latest geocode completes, then open form
  const openAddressForm = async () => {
    try {
      await geocodeLatLng(mapCenter); // wait for geocode to prefill fields
    } catch (err) {
      console.error(err);
    } finally {
      setShowmap(false);
    }
  };

  const update_location = () => {
    if (!floor) {
      toast.error("Floor is required");
      return;
    }
    if (!area) {
      toast.error("Area is required");
      return;
    }
    if (!district) {
  toast.error("District is required");
  return;
}
    if (!pincode) {
      toast.error("Pincode is required");
      return;
    }
    if (!city) {
      toast.error("City is required");
      return;
    }
    if (!state) {
      toast.error("State is required");
      return;
    }
    if (!country) {
      toast.error("Country is required");
      return;
    }

    // Build your own one-line address
    const fulladdress = [floor, area, city, state, country, pincode]
      .filter(Boolean)
      .join(", ");

    const final_location = {
      floor,
      area,
      pincode,
      city,
      state,
      country,
      latitude: mapCenter.lat,
      longitude: mapCenter.lng,
      fulladdress, 
      district,
    };

    onAddressChange(final_location);
  };
  const fetchPincodeInfo = async (pin) => {
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await response.json();
      const entry = Array.isArray(data) ? data[0] : null;

      if (entry?.Status !== "Success" || !entry?.PostOffice?.length) return null;

      const offices = entry.PostOffice;
      return {
        districts: mergeDistrictOptions(offices.map((office) => office.District)),
        state: offices[0].State || "",
        country: offices[0].Country || "",
      };
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const fetchStateCities = async (countryName, stateName) => {
    const cacheKey = `${countryName}|${stateName}`.toLowerCase();
    if (stateCitiesCacheRef.current.has(cacheKey)) {
      return stateCitiesCacheRef.current.get(cacheKey);
    }

    try {
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/state/cities",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            country: countryName,
            state: stateName,
          }),
        }
      );

      const data = await response.json();
      const cities = Array.isArray(data?.data) ? data.data : [];
      stateCitiesCacheRef.current.set(cacheKey, cities);
      return cities;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // The pincode is what actually identifies the district, so resolve it from the
  // postal API and ignore any response that belongs to a pincode we moved on from.
  useEffect(() => {
    const pin = (pincode || "").trim();
    const hint = geocodeDistrict;
    const seq = pincodeLookupSeqRef.current + 1;
    pincodeLookupSeqRef.current = seq;

    if (!/^\d{6}$/.test(pin)) {
      resolvedDistrictRef.current = { pin: "", district: "" };
      preferredDistrictsRef.current = hint ? [hint] : [];
      setDistricts(
        mergeDistrictOptions(preferredDistrictsRef.current, stateCitiesRef.current)
      );
      return;
    }

    fetchPincodeInfo(pin).then((info) => {
      if (seq !== pincodeLookupSeqRef.current) return;

      const resolved = pickDistrict(info?.districts, hint);
      resolvedDistrictRef.current = { pin, district: resolved };
      preferredDistrictsRef.current = mergeDistrictOptions(
        [resolved, ...(info?.districts || []), hint].filter(Boolean)
      );
      setDistricts(
        mergeDistrictOptions(preferredDistrictsRef.current, stateCitiesRef.current)
      );

      if (resolved) setDistrict(resolved);
      if (info?.state) setState(info.state);
      if (info?.country) setCountry(info.country);
    });
  }, [pincode, geocodeDistrict]);

  // Option list for the field: every city/district the state has, plus whatever
  // the pincode lookup resolved so the detected value is always selectable.
  useEffect(() => {
    const stateName = (state || "").trim();
    const countryName = (country || "").trim() || "India";
    const seq = districtOptionsSeqRef.current + 1;
    districtOptionsSeqRef.current = seq;
    stateCitiesRef.current = [];

    if (!stateName) {
      setDistricts(mergeDistrictOptions(preferredDistrictsRef.current));
      return;
    }

    fetchStateCities(countryName, stateName).then((cities) => {
      if (seq !== districtOptionsSeqRef.current) return;
      stateCitiesRef.current = cities;
      setDistricts(mergeDistrictOptions(preferredDistrictsRef.current, cities));
    });
  }, [state, country]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return (
    <>
      {showmap ? (
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: 10,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              height: "48px",
              width: "90%",
              maxWidth: "400px",
              display: "flex",
              alignItems: "center",
              background: "var(--app-surface)",
              borderRadius: "8px",
              padding: "6px 10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
          >
            <i
              className="fa fa-search"
              style={{
                marginRight: "10px",
                color: "var(--app-text-muted)",
                flexShrink: 0,
              }}
            ></i>
            {/* the wrapper carries the flex sizing; without it the input keeps its
                default width and the typed address gets clipped */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <GAutocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={handlePlaceChanged}
                fields={AUTOCOMPLETE_FIELDS}
              >
                <input
                  type="text"
                  placeholder="Search location"
                  style={{
                    width: "100%",
                    padding: 0,
                    fontSize: "16px",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: "var(--app-text)",
                    textOverflow: "ellipsis",
                  }}
                />
              </GAutocomplete>
            </div>
          </div>

          <button
            onClick={() => {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  };
                  setMapCenter(pos);
                  setMarkerPosition(pos);
                  geocodeLatLng(pos).catch((err) => console.error(err));
                },
                (error) => {
                  console.error("Error getting location", error);
                  alert("Failed to detect location.");
                },
              );
            }}
            style={{
              position: "absolute",
              bottom: 150,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              padding: "10px 16px",
              backgroundColor: "var(--app-surface)",
              color: "#4369CE",
              border: "1px solid #4369CE",
              borderRadius: "9px",
              fontWeight: "700",
              fontSize: "14px",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
          >
            <Icon icon="mage:location-fill" width="20" height="20" />
            &nbsp;DETECT MY LOCATION
          </button>

          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={17}
            onLoad={onLoad}
            onIdle={handleMapIdle}
            options={{
              disableDefaultUI: true,
              zoomControl: false,
            }}
          />

          <div
            style={{
              position: "absolute",
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -100%)",
              zIndex: 1000,
              color: "#E72020",
              pointerEvents: "none",
            }}
          >
            <Icon icon="majesticons:map-marker" width="44" height="44" />
          </div>

          <div style={{ marginTop: "15px", fontSize: "16px" }}>
            <strong>
              <Icon icon="weui:location-filled" width="24" height="24" />{" "}
            </strong>{" "}
            {address || "Fetching address..."}
          </div>
          <br />
          <button type="button"
            onClick={openAddressForm}
            className="btn btn-added-primary w-100 m-10"
            style={{ marginBottom: "10px" }}
          >
            Add Complete Address
          </button>
        </div>
      ) : (
        <div className="container-fluid">
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "80px",
              borderRadius: "10px",
            }}
            center={mapCenter}
            zoom={17}
            options={{
              disableDefaultUI: true,
              zoomControl: false,
              streetViewControl: false,
              fullscreenControl: false,
            }}
          >
            <MarkerF
              position={markerPosition}
              icon={{
                url:
                  "data:image/svg+xml;charset=UTF-8," +
                  encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" fill="red" viewBox="0 0 24 24">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 4.61 7 13 7 13s7-8.39 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5z"/>
      </svg>
    `),
                scaledSize: new window.google.maps.Size(48, 48),
                anchor: new window.google.maps.Point(22, 44),
              }}
            />
          </GoogleMap>

          <div className="row" style={{ marginTop: "20px" }}>
            <div className="col-12">
              <div className="form-group">
                <label>
                  Flat/Floor/Building<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={floor}
                  onChange={(e) => setFloor(e.target.value)}
                  className="form-control"
                  placeholder="Enter Flat/Floor/Building"
                />
              </div>
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Area/Locality<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="form-control"
                  placeholder="Enter Area/Locality"
                />
              </div>
            </div>

            <div className="col-6">
              <div className="form-group">
                <label>
                  City<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={city}
                  className="form-control"
                  placeholder="Enter City"
                  disabled
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label htmlFor={districtInputId}>
                  District<span className="text-danger">*</span>
                </label>

                <DistrictCombobox
                  id={districtInputId}
                  value={district}
                  options={districts}
                  onChange={setDistrict}
                />
              </div>
            </div>

            <div className="col-6">
              <div className="form-group">
                <label>
                  State<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={state}
                  className="form-control"
                  placeholder="Enter State"
                  disabled
                />
              </div>
            </div>

            <div className="col-6">
              <div className="form-group">
                <label>
                  Country<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={country}
                  className="form-control"
                  placeholder="Enter Country"
                  disabled
                />
              </div>
            </div>

            <div className="col-6">
              <div className="form-group">
                <label>
                  Pincode<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  className="form-control"
                  placeholder="Enter Pincode"
                />
              </div>
            </div>

            <div className="col-12">
              <button type="button"
                onClick={() => {
                  update_location();
                }}
                className="btn btn-added-primary w-100 mt-20"
              >
                Save Address
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleMapWithSearch;
